// /app/frontend/src/components/frugal-v2/components/reports/AccountsBlock.jsx
// Banks, combined balance stats, cards, and net position blocks (extracted from MonthSummaryModal)

import React from 'react';
import { Building2, CreditCard } from 'lucide-react';
import ReportSection from './ReportSection';

const AccountsBlock = ({ accounts, t }) => {
  const hasBanks = accounts.banks?.length > 0;
  const hasCards = accounts.cards?.length > 0;

  return (
    <>
      {hasBanks && (
        <ReportSection title={t.banks} icon={Building2}>
          <div className="space-y-2">
            {accounts.banks.map((bank, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-white">
                  {bank.name}
                  {bank.is_default && <span className="text-xs text-emerald-400"> (Default)</span>}
                </span>
                <span className="text-emerald-400 font-medium">${bank.balance?.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 font-semibold">
              <span className="text-emerald-400">{t.totalBankBalance}</span>
              <span className="text-white">${accounts.total_bank_balance?.toFixed(2)}</span>
            </div>
          </div>
        </ReportSection>
      )}

      {hasBanks && accounts.balance_stats && (
        <div className="bg-gradient-to-br from-sky-500/10 to-emerald-500/10 border border-sky-500/20 rounded-xl p-4" data-testid="bank-balance-stats-card">
          <div className="text-sm text-slate-300 mb-3 font-medium">
            {t.balanceStatsTitle}{' '}
            <span className="text-xs text-slate-500">({accounts.balance_stats.days} {t.daysLabel})</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/5 rounded-lg px-3 py-2.5">
              <div className="text-[11px] text-slate-400">{t.maxBalance}</div>
              <div className="text-emerald-300 font-semibold text-sm">${accounts.balance_stats.max?.toFixed(2)}</div>
            </div>
            <div className="bg-white/5 rounded-lg px-3 py-2.5">
              <div className="text-[11px] text-slate-400">{t.minBalance}</div>
              <div className={`font-semibold text-sm ${accounts.balance_stats.min < 0 ? 'text-rose-400' : 'text-amber-300'}`}>
                ${accounts.balance_stats.min?.toFixed(2)}
              </div>
            </div>
            <div className="bg-white/5 rounded-lg px-3 py-2.5">
              <div className="text-[11px] text-slate-400">{t.avgBalance}</div>
              <div className="text-sky-300 font-semibold text-sm">${accounts.balance_stats.avg?.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {hasCards && (
        <ReportSection title={t.cards} icon={CreditCard}>
          <div className="space-y-2">
            {accounts.cards.map((card, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-white">
                  {card.name}
                  {card.is_default && <span className="text-xs text-amber-400"> (Default)</span>}
                </span>
                <span className="text-red-400 font-medium">${card.balance?.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 font-semibold">
              <span className="text-red-400">{t.totalCardDebt}</span>
              <span className="text-white">${accounts.total_card_balance?.toFixed(2)}</span>
            </div>
          </div>
        </ReportSection>
      )}

      {(hasBanks || hasCards) && (
        <div className={`rounded-xl p-4 border ${accounts.net_position >= 0 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
          <div className="flex items-center justify-between">
            <span className="text-slate-300">{t.netPosition}</span>
            <span className={`text-2xl font-bold ${accounts.net_position >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              ${accounts.net_position?.toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountsBlock;
